import { useMemo } from 'react';
import type { TerminalParamDescriptor } from '@reform-lite/contracts';
import { useT } from '@/i18n/use-t';
import type { TerminalParamAssignment } from '@/hooks/use-terminal-params';

/**
 * The one permitted key-specific view in the parameter editor (RL-280,
 * TP-FR-015).
 *
 * `returns.reasons` is a `string` holding a JSON tree projected from GP
 * Universal API `SALESDOCS`. A generic string field cannot infer that shape,
 * and rendering the raw JSON would leave an operator debugging an empty
 * return-reason list unable to read what the till actually holds. So this
 * key alone gets a nested rendering — read-only, because the descriptor is
 * not authorable (TP-FR-014); the projection owns the value.
 *
 * Anything else that wants its own widget needs its own value type in the
 * contract, not a second entry here.
 */

export type ReturnReasonNode = {
  code: string;
  label: string;
  children: ReturnReasonNode[];
};

function isReturnReasonNode(value: unknown): value is ReturnReasonNode {
  if (value === null || typeof value !== 'object') return false;
  const node = value as Record<string, unknown>;
  return (
    typeof node['code'] === 'string' &&
    typeof node['label'] === 'string' &&
    Array.isArray(node['children']) &&
    node['children'].every(isReturnReasonNode)
  );
}

export function parseReturnReasonDirectory(value: unknown): ReturnReasonNode[] | null {
  if (typeof value !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) && parsed.every(isReturnReasonNode) ? parsed : null;
  } catch {
    return null;
  }
}

function ReturnReasonTree({ nodes }: { nodes: ReturnReasonNode[] }) {
  const t = useT();
  return (
    <ul
      className="space-y-1 pl-4"
      aria-label={t('parameters.returns.treeLabel', 'Imported return reasons')}
    >
      {nodes.map((node) => (
        <li key={`${node.code}:${node.label}`}>
          <span className="font-medium">{node.label}</span>{' '}
          <span className="font-mono text-xs text-muted-foreground">{node.code}</span>
          {node.children.length > 0 && <ReturnReasonTree nodes={node.children} />}
        </li>
      ))}
    </ul>
  );
}

export function ReturnReasonDirectoryView({
  descriptor,
  assignment,
}: {
  descriptor: TerminalParamDescriptor;
  assignment: TerminalParamAssignment | undefined;
}) {
  const t = useT();
  const nodes = useMemo(
    () => parseReturnReasonDirectory(assignment?.value ?? descriptor.defaultValue),
    [assignment?.value, descriptor.defaultValue],
  );
  if (assignment === undefined) {
    return (
      <p className="text-sm text-muted-foreground" aria-label={descriptor.label}>
        {t('parameters.returns.none', 'No return reasons have been imported from GP yet.')}
      </p>
    );
  }
  if (nodes === null) {
    return (
      <p className="text-sm text-destructive" aria-label={descriptor.label}>
        {t(
          'parameters.returns.malformed',
          'The imported return reason directory is malformed. Wait for the next full GP refresh.',
        )}
      </p>
    );
  }
  return (
    <div className="space-y-2" aria-label={descriptor.label}>
      {nodes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('parameters.returns.empty', 'The imported return reason directory is empty.')}
        </p>
      ) : (
        <div className="rounded border bg-background p-3 text-sm">
          <ReturnReasonTree nodes={nodes} />
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {t('parameters.returns.lastImported', 'Last imported')}{' '}
        {new Date(assignment.updatedAt).toLocaleString()}.
      </p>
    </div>
  );
}
